/* eslint-disable react-hooks/exhaustive-deps */
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useQueryClient } from 'react-query';
import { useRouteMatch } from 'react-router-dom';
import AppContext from '../../contexts/AppContext';
import AppDivisionContext from '../../contexts/AppDivisionContext';
import ProjectContext from '../../contexts/ProjectContext';
import ProjectPermissionContext, {
  ProjectPermissionContextProvider,
} from '../../contexts/ProjectPermissionContext';
import RolesContext from '../../contexts/RolesContext';
import { setProjectInspectionsQueryData } from '../../hooks/inspection';
import { useProject } from '../../hooks/project';
import uploadApi from '../../utils/api/upload';
import { projectTabs } from '../../utils/tabs/projectTabs';
import { getProjectConfigHelpers } from './Forms/projectFormSchema';
import NavTabs from '../Shared/NavTabs';
import Error from '../Shared/ResourceIndex/Error';
import Loading from '../Shared/ResourceIndex/Loading';
import ProjectHeader from './Header/ProjectHeader';
import { clientApi } from '../../utils/api/client';
import TenantFeatureContext from '../../contexts/TenantContext';
import { useOffline } from '../../contexts/OfflineContext';
import permitApi from '../../utils/api/permit';
import schedulerApi from '../../utils/api/inspectionSchedular';
import { faInfoCircle } from '@fortawesome/free-solid-svg-icons';

const ProjectDetailUI = ({ ...props }) => {
  const match = useRouteMatch();
  const queryClient = useQueryClient();
  const projectQuery = useProject(match.params.id);
  const [clientTab, setClientTab] = useState([]);
  const [customTab, setCustomTab] = useState();
  const [clientName, setClientName] = useState('');
  const [finalTabslist, setFinalTabslist] = useState([]);
  const [clientPublicViewTabIds, setClientPublicViewTabIds] = useState(null);
  const offline = useOffline();

  const appContext = useContext(AppContext);
  const featureContext = useContext(TenantFeatureContext);
  const hasAssetTrackingFeature =
    featureContext.tenantHasFeature('Asset Tracking');
  const projectContext = useContext(ProjectContext);
  const { getPath } = useContext(AppDivisionContext);
  const projectPermissionContext = useContext(ProjectPermissionContext);
  const loaded = useMemo(() => {
    return projectContext.project; // && projectQuery.isFetched;
  }, [projectContext.project, projectQuery.isLoading, projectQuery.isFetching]);
  const error = useMemo(() => {
    if (projectQuery.isError) {
      return 'Error loading project' + projectQuery.error;
    } else {
      return null;
    }
  }, [projectQuery.isError, projectQuery.error]);

  useEffect(() => {
    const clientId = loaded && projectContext.project.client_id;

    if (clientId) {
      clientApi.show(clientId).then((res) => {
        setCustomTab(res.custom_tab);
        setClientName(res.name);
      });

      if (offline) {
        setClientTab([{ tabs: { name: 'All Inspections' } }]);

        return;
      }

      clientApi.getClientsProjectTab.index(clientId).then((res) => {
        setClientTab(res);
      });

      clientApi.getClientsPublicViewTab.index(clientId).then((res) => {
        setClientPublicViewTabIds(
          res.length > 0
            ? res.map((r) => r.tabs && r.tabs.name).filter(Boolean)
            : null,
        );
      });
    }
  }, [loaded, projectContext.project]);

  useEffect(() => {
    let mounted = true;

    getCustomTabs().then((finalTablist) => {
      if (mounted) setFinalTabslist(finalTablist);
    });

    return () => {
      mounted = false;
    };
  }, [customTab, clientTab, clientPublicViewTabIds, hasAssetTrackingFeature]);

  const showUpdateForm = false;

  const getCustomTabs = async () => {
    const statusOfPermit = await permitApi.getProjectPermitStatus(
      match.params.id,
    );

    let assetTrackingEnabledOnForm = false;

    if (hasAssetTrackingFeature) {
      try {
        const forms = await schedulerApi.getProjectForms(match.params.id);

        assetTrackingEnabledOnForm = Boolean(
          forms?.success &&
          forms.data?.some((form) => form.is_asset_tracking_enabled),
        );
      } catch (e) {
        assetTrackingEnabledOnForm = false;
      }
    }

    if (offline) {
      return projectTabs.map((ele) => {
        if (ele.label === 'All Inspections') {
          return {
            ...ele,
            showTab: () => true,
          };
        }

        return {
          ...ele,
          showTab: () => false,
        };
      });
    }

    let roleBaseProjectTabs = projectTabs.filter((e) => {
      if (e.label === 'Project Settings') {
        return props.permCheck('update', 'Project');
      }

      if (e.label === 'Assets') {
        return props.permCheck('read', 'Project');
      }

      return props.permCheck('all', `${e.label} Tab`);
    });

    // Apply special Project Documents logic to all cases
    const applyProjectDocumentsConfig = (ele) => {
      return {
        ...ele,
        isIcon: statusOfPermit.status !== 'Ok',
        icon: faInfoCircle,
        iconColor: statusOfPermit.status === 'Expired' ? '#FF4C51' : '#FF9F43',
        iconTooltip:
          statusOfPermit.status === 'Expired'
            ? 'There are permit(s) that have expired!'
            : 'There are permit(s) that are expiring soon!',
        showTab: () => true,
      };
    };

    // Compute project_config restrictions up front — applied in all paths including customTab
    const { hasDocManagement, hasInAppInspections, hasAnyInspections } =
      getProjectConfigHelpers(projectContext.project?.project_config || 'full');

    const restricted_tabs = [];

    if (!hasDocManagement) restricted_tabs.push('Site Photos', 'BMP Details');
    if (!hasInAppInspections) restricted_tabs.push('Findings');
    if (!hasAnyInspections) restricted_tabs.push('All Inspections');
    if (!featureContext.tenantHasFeature('Enable Photo Uploads'))
      restricted_tabs.push('Site Photos');
    if (!hasAssetTrackingFeature || !assetTrackingEnabledOnForm)
      restricted_tabs.push('Assets');

    const isAssetTrackingActive =
      hasAssetTrackingFeature && assetTrackingEnabledOnForm;

    // Public view tab filter — computed up front so all paths apply it
    const isPublicView = window.location.pathname.startsWith('/public');
    const applyPublicViewFilter = (tabs) => {
      if (!isPublicView || clientPublicViewTabIds === null) return tabs;

      return tabs.map((ele) =>
        clientPublicViewTabIds.includes(ele.label)
          ? ele
          : { ...ele, showTab: () => false },
      );
    };

    if (customTab) {
      if (!clientTab.length) {
        return applyPublicViewFilter(
          roleBaseProjectTabs.map((ele) => {
            if (restricted_tabs.includes(ele.label)) {
              return { ...ele, showTab: () => false };
            }
            if (ele.label === 'Assets' && isAssetTrackingActive) {
              return ele;
            }
            if (ele.label === 'Project Settings') {
              return {
                ...ele,
                showTab: () => props.permCheck('update', 'Project'),
              };
            }
            if (ele.label === 'Project Documents') {
              return applyProjectDocumentsConfig(ele);
            }

            return { ...ele, showTab: () => false };
          }),
        );
      }
      if (clientTab.length) {
        const clientTabName = clientTab.map((ele) => ele.tabs.name);

        return applyPublicViewFilter(
          roleBaseProjectTabs.map((ele) => {
            if (restricted_tabs.includes(ele.label)) {
              return { ...ele, showTab: () => false };
            }
            if (ele.label === 'Assets' && isAssetTrackingActive) {
              return ele;
            }
            if (ele.label === 'Project Settings') {
              return {
                ...ele,
                showTab: () => props.permCheck('update', 'Project'),
              };
            }
            if (ele.label === 'Project Documents') {
              return applyProjectDocumentsConfig(ele);
            } else if (!clientTabName.includes(ele.label)) {
              return { ...ele, showTab: () => false };
            }

            return ele;
          }),
        );
      }
    }

    roleBaseProjectTabs = roleBaseProjectTabs.map((ele) => {
      if (ele.label === 'Project Documents') {
        return applyProjectDocumentsConfig(ele);
      }
      if (restricted_tabs.includes(ele.label)) {
        return {
          ...ele,
          showTab: () => false,
        };
      }
      if (!ele.showTab) {
        return {
          ...ele,
          showTab: () => true,
        };
      }

      return ele;
    });

    return applyPublicViewFilter(roleBaseProjectTabs);
  };

  const onDocumentDelete = (guid) => {
    const { projectDocs, updateProjectDocs } = projectContext;

    updateProjectDocs({
      documentTypes: projectDocs.documentTypes.map((docType) => ({
        ...docType,
        documents: docType.documents.filter((doc) => doc.GUID !== guid),
      })),
    });
  };

  const onMove = async ({ document, dragIndex, hoverIndex }, group) => {
    const { projectDocs, updateProjectDocs } = projectContext;
    const documentTypes = projectDocs.documentTypes;
    const documentTypeIndex = documentTypes.findIndex(
      (documentType) => documentType.name === group,
    );

    if (documentTypeIndex < 0) {
      return;
    }

    const docType = documentTypes[documentTypeIndex];
    const draggingDocument = docType.documents[dragIndex];

    docType.documents.splice(dragIndex, 1);

    docType.documents.splice(hoverIndex, 0, draggingDocument);

    updateProjectDocs({ documentTypes });
  };

  const onDrop = ({ document, index }, group) => {
    const model = document.ProjectUploadModel || document.project_uploads;

    return uploadApi.project.sort({
      id: model.id,
      order: index,
      projectId: model.project_id,
      projectDocTypeId: model.project_doc_type_id,
    });
  };

  const deleteProject = () => {
    props.history.push(getPath('/projects'));
  };

  const shouldRenderRegulations = () => props.permCheck('read', 'Regulations');

  useEffect(() => {
    if (
      props.location.state &&
      props.location.state.project &&
      props.location.state.project.id &&
      props.location.state.project.inspections
    ) {
      projectContext.setProject(props.location.state.project);

      setProjectInspectionsQueryData({
        queryClient,
        projectId: props.location.state.project.id,
        data: props.location.state.project.inspections,
      });
    }

    return () => projectContext.clear();
  }, []);

  useEffect(() => {
    if (!projectQuery.isFetching) {
      projectContext.setProject(projectQuery.data);
    }
  }, [projectQuery.isFetching]);

  useEffect(() => {
    if (loaded && projectContext.project && projectContext.project.archivedAt) {
      projectPermissionContext.setReadOnly(true);
    } else {
      projectPermissionContext.setReadOnly(false);
    }
  }, [loaded]);

  return (
    <section className='project-detail'>
      {error ? <Error error={error} /> : null}
      {loaded && clientTab ? (
        <React.Fragment>
          <ProjectHeader onDelete={deleteProject} clientName={clientName} />
          <NavTabs
            tabs={finalTabslist}
            getPropFuncArgs={() => {
              // REFACTOR
              return [
                {
                  contacts: [],
                  showUpdateForm,
                  loaded,
                  error,
                },
                {
                  onDocumentUpload: (search = '') =>
                    appContext.loadData(
                      () =>
                        projectContext.loadDocs(projectQuery.data.id, search),
                      'Documents',
                    ),
                  onDocumentDelete: onDocumentDelete,
                  shouldRenderRegulations: shouldRenderRegulations,
                  context: projectContext,
                  onMove: onMove,
                  onDrop: onDrop,
                },
              ];
            }}
            url={match.url}
          />
        </React.Fragment>
      ) : (
        <Loading />
      )}
    </section>
  );
};

export const ProjectDetail = (props) => {
  const permCheck = useContext(RolesContext).userHasPermission;

  return (
    <ProjectPermissionContextProvider>
      <ProjectDetailUI {...props} permCheck={permCheck} />
    </ProjectPermissionContextProvider>
  );
};

export default ProjectDetail;
